import { add, format } from '@sequencehq/utils/dates'
import { match, P } from 'ts-pattern'
import { DATE_FORMAT_API } from 'Invoices/constants'
import type {
  DueDatePolicy,
  InvoiceDueDateCalculatedFrom
} from 'Invoices/types'

export type InvoiceDueDateResolution = {
  isMerchantDefault: boolean
} & (
  | { type: 'RESOLVED'; dueDate: string; isInferred: boolean }
  | { type: 'PENDING'; paymentTermDays: number }
)

/**
 * The due date to show for an invoice, inferred from the policy whenever the
 * anchor to count from is known.
 *
 *   dueDate set            -> RESOLVED -> always used on issue
 *   ACCOUNTING_DATE mode   -> RESOLVED -> can infer dueDate from accounting date and terms
 *   ISSUE_DATE mode        -> PENDING  -> backend calculates on issue
 *
 * Returns `isMerchantDefault: true` when there is no `dueDatePolicy` or `paymentTermDays` on the invoice,
 * so the merchant default is applied.
 */
export const resolveInvoiceDueDate = ({
  calculatedFrom,
  dueDate,
  dueDatePolicy,
  paymentTermDays,
  accountingDate,
  defaultDueDateDays
}: {
  calculatedFrom: InvoiceDueDateCalculatedFrom
  dueDate: string | undefined
  dueDatePolicy: DueDatePolicy | null
  paymentTermDays: number | null
  accountingDate: string
  defaultDueDateDays: number
}): InvoiceDueDateResolution =>
  match({ dueDate, dueDatePolicy })
    // A stored due date stands, whichever mode the invoice was created in
    .with({ dueDate: P.string.minLength(1) }, ({ dueDate }) =>
      asStored(dueDate)
    )
    // Defensive: a manual policy always comes with a stored due date
    .with({ dueDatePolicy: { type: 'MANUAL' } }, ({ dueDatePolicy }) =>
      asStored(dueDatePolicy.date)
    )
    .with({ dueDatePolicy: { type: 'PAYMENT_TERMS' } }, ({ dueDatePolicy }) =>
      fromTerms({
        calculatedFrom,
        accountingDate,
        termDays: dueDatePolicy.paymentTermDays,
        isMerchantDefault: false
      })
    )
    // Legacy invoices can carry their own terms with no policy at all
    .with({ dueDatePolicy: P.nullish }, () =>
      fromTerms({
        calculatedFrom,
        accountingDate,
        termDays: paymentTermDays ?? defaultDueDateDays,
        isMerchantDefault: paymentTermDays === null
      })
    )
    .exhaustive()

const asStored = (dueDate: string): InvoiceDueDateResolution => ({
  type: 'RESOLVED',
  dueDate,
  isInferred: false,
  isMerchantDefault: false
})

const fromTerms = ({
  calculatedFrom,
  accountingDate,
  termDays,
  isMerchantDefault
}: {
  calculatedFrom: InvoiceDueDateCalculatedFrom
  accountingDate: string
  termDays: number
  isMerchantDefault: boolean
}): InvoiceDueDateResolution =>
  match(calculatedFrom)
    .with('ACCOUNTING_DATE', () => ({
      type: 'RESOLVED' as const,
      dueDate: format(
        add(new Date(accountingDate), { days: termDays }),
        DATE_FORMAT_API
      ),
      isInferred: true,
      isMerchantDefault
    }))
    // Nothing to count from until the backend issues the invoice
    .with('ISSUE_DATE', () => ({
      type: 'PENDING' as const,
      paymentTermDays: termDays,
      isMerchantDefault
    }))
    .exhaustive()
